<template>
  <div class="card-table">
    <!-- Card Header -->
    <div class="card-header">
      <div class="card-header-left">
        <h3 class="card-title">{{ title }}</h3>
        <span v-if="subtitle" class="card-subtitle">{{ subtitle }}</span>
      </div>
      <div class="card-header-right">
        <slot name="controls"></slot>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body" :class="{ 'scrollable': stickyFirstColumn }">
      <table v-if="rows && rows.length > 0" class="data-table" :class="{ 'sticky-first-col': stickyFirstColumn }">
        <thead>
          <tr>
            <th v-if="variant === 'ranked'" class="rank-column">#</th>
            <th v-for="col in columns" :key="col.key" :class="col.align ? `align-${col.align}` : ''">
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
              v-for="(row, index) in displayRows"
              :key="index"
              :class="{
                'summary-row': variant === 'summary' && index === displayRows.length - 1 && hasSummary,
                'clickable-row': clickable && !(variant === 'summary' && index === displayRows.length - 1 && hasSummary)
              }"
              @click="handleRowClick(row, index)"
            >
            <td v-if="variant === 'ranked'" class="rank-column">{{ index + 1 }}</td>
            <td v-for="col in columns" :key="col.key" :class="col.align ? `align-${col.align}` : ''">
              <!-- Status badge -->
              <span v-if="variant === 'status' && col.key === statusField" class="status-badge" :class="getStatusClass(row[col.key])">
                {{ row[col.key] }}
              </span>
              <!-- Currency format -->
              <template v-else-if="col.format === 'currency'">
                {{ formatCurrency(row[col.key]) }}
              </template>
              <!-- Number format -->
              <template v-else-if="col.format === 'number'">
                {{ formatNumber(row[col.key]) }}
              </template>
              <!-- Default -->
              <template v-else>
                {{ row[col.key] }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="table-empty">No data available</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardTable',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    columns: {
      type: Array,
      required: true
      // Format: [{ key: 'name', label: 'Name', align: 'left', format: 'currency' }]
    },
    rows: {
      type: Array,
      default: () => []
    },
    variant: {
      type: String,
      default: 'ranked',
      validator: (v) => ['ranked', 'status', 'summary'].includes(v)
    },
    // Status variant props
    statusField: {
      type: String,
      default: 'status'
    },
    statusMap: {
      type: Object,
      default: () => ({
        'Paid': 'success',
        'Pending': 'warning',
        'Overdue': 'danger',
        'Active': 'success',
        'Inactive': 'neutral'
      })
    },
    // Summary variant props
    summaryRow: {
      type: Object,
      default: null
    },
    maxRows: {
      type: Number,
      default: 10
    },
    clickable: {
      type: Boolean,
      default: false
    },
    stickyFirstColumn: {
      type: Boolean,
      default: false
    }
  },
  emits: ['row-click'],
  computed: {
    hasSummary() {
      return this.variant === 'summary' && this.summaryRow;
    },
    displayRows() {
      let result = this.rows.slice(0, this.maxRows);
      if (this.hasSummary) {
        result = [...result, this.summaryRow];
      }
      return result;
    }
  },
  methods: {
    getStatusClass(status) {
      const type = this.statusMap[status] || 'neutral';
      return `status-${type}`;
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('en-US').format(value);
    },
    handleRowClick(row, index) {
      // Don't emit click for summary row
      if (this.clickable && !(this.variant === 'summary' && index === this.displayRows.length - 1 && this.hasSummary)) {
        this.$emit('row-click', row);
      }
    }
  }
}
</script>

<style scoped>
.card-table {
  background: #FFF;
  overflow: hidden;
}

.card-header {
  padding: 16px;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.card-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.card-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #666;
}

.card-body {
  padding: 0;
}

.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
}

.data-table th {
  padding: 0 16px;
  text-align: left;
  font-weight: 600;
  color: #666;
  background: #fff;
  height: 50px;
  box-sizing: border-box;
  white-space: nowrap;
  position: relative;
}

/* Partial-height border between header cells */
.data-table th:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 25%;
  height: 50%;
  width: 1px;
  background-color: #e5e7eb;
}

/* Bottom border for header row */
.data-table thead tr {
  border-bottom: 1px solid #f0f0f0;
}

.data-table th {
  border-bottom: 1px solid #f0f0f0;
}

.data-table td {
  padding: 0 16px;
  color: #262626;
  background-color: #fff;
  height: 50px;
  box-sizing: border-box;
  border-bottom: 1px solid #f0f0f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-table tr:last-child td {
  border-bottom: none;
}

.data-table tbody tr:hover td {
  background-color: #f9fafb;
}

.data-table tbody tr.clickable-row {
  cursor: pointer;
}

.data-table tbody tr.clickable-row:hover td {
  background-color: #f3f4f6;
}

.rank-column {
  width: 40px;
  text-align: left;
  color: #9ca3af;
  font-weight: 500;
}

.align-left { text-align: left; }
.align-center { text-align: left; }
.align-right { text-align: left; }

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: var(--border-radius);
  font-size: 12px;
  font-weight: 500;
}

.status-success {
  background: rgba(5, 150, 105, 0.1);
  color: #059669;
}

.status-warning {
  background: rgba(217, 119, 6, 0.1);
  color: #d97706;
}

.status-danger {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.status-neutral {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

/* Summary row */
.summary-row td {
  font-weight: 600;
  background: #fff !important;
}

.table-empty {
  padding: 32px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

/* Horizontal scrolling with sticky first column */
.card-body.scrollable {
  overflow-x: auto;
  overflow-y: visible;
}

.data-table.sticky-first-col {
  min-width: max-content;
}

.data-table.sticky-first-col th:first-child,
.data-table.sticky-first-col td:first-child {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  font-weight: 600;
  width: 300px;
  min-width: 300px;
  max-width: 300px;
}

.data-table.sticky-first-col th:first-child {
  z-index: 3;
}

.data-table.sticky-first-col tbody tr:hover td:first-child {
  background-color: #f9fafb;
}

.data-table.sticky-first-col tbody tr.clickable-row:hover td:first-child {
  background-color: #f3f4f6;
}

.data-table.sticky-first-col .summary-row td:first-child {
  background: #fff;
}
</style>
